import { useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { describeCacheAge } from '../utils/cacheAge';

/**
 * Everything the app header shows and does, in one place, so the current (v1)
 * page headers and the redesigned (v2) top bar cannot drift apart:
 *
 * - dealership: the dealership in view. The active workspace wins over the
 *   caller's own org from /me (SAG staff look at a customer's data).
 * - readOnly / canSwitch / switchDealership: from WorkspaceContext; the switch
 *   opens the same "Choose a dealership" picker flow.
 * - cacheStatus: the "Updated Xm ago" badge, amber when stale.
 * - refresh / refreshing: the forced inventory reload (loadInventory(true)).
 *
 * `data` is the DataContext value (or null outside a DataProvider); the
 * caller passes it so pages keep reading DataContext the way they do today.
 */
export default function useAppChrome({ orgName, data } = {}) {
    const { activeWorkspace, readOnly, canSwitch, requestWorkspace } = useWorkspace();
    const cacheInfo = data?.cacheInfo || null;
    const loadInventory = data?.loadInventory;

    const switchDealership = useCallback(() => requestWorkspace(), [requestWorkspace]);
    const refresh = useCallback(() => loadInventory?.(true), [loadInventory]);

    // Recomputed on every render, exactly like the header did inline.
    const cacheStatus = describeCacheAge(cacheInfo);

    return {
        dealership: activeWorkspace?.name || orgName,
        readOnly: Boolean(readOnly),
        canSwitch: Boolean(canSwitch),
        switchDealership,
        requestWorkspace,
        cacheInfo,
        cacheStatus,
        refresh,
        refreshing: Boolean(data?.inventoryLoading),
    };
}
